import React, {
  useRef,
  useMemo,
  useEffect,
  useContext,
  createContext,
} from "react";

export const NOTE_SAVE_HANDLERS = {
  REPORT_NOTE_SAVE: "reportNoteSave",
  REPORT_NOTE_SAVE_AND_PROCESS: "reportNoteSaveAndProcess",
  REPORT_NOTE_ARCHIVE: "reportNoteArchive",
  CLINIC_NOTE_SAVE: "clinicNoteSave",
  PROVIDER_NOTE_SAVE: "providerNoteSave",
  CLINIC_NOTE_UNSAVED: "clinicNoteUnsaved",
  PROVIDER_NOTE_UNSAVED: "providerNoteUnsaved",
  REPORT_NOTE_UNSAVED: "reportNoteUnsaved",
  BILLING_DATE_UNSAVED: "billingDateUnsaved",
};

const NoteSaveHandlersContext = createContext(null);

export const NoteSaveHandlersProvider = ({ children }) => {
  const handlersRef = useRef({});

  const registry = useMemo(
    () => ({
      register: (name, handler) => {
        handlersRef.current[name] = handler;

        return () => {
          if (handlersRef.current[name] === handler) {
            delete handlersRef.current[name];
          }
        };
      },
      // true when a handler ran (it may still refuse an invalid form), false when none is registered
      run: (name, ...args) => {
        const handler = handlersRef.current[name];
        if (!handler) return false;

        handler(...args);
        return true;
      },
      // Returns the handler's result (undefined if none)
      call: (name, ...args) => handlersRef.current[name]?.(...args),
    }),
    []
  );

  return (
    <NoteSaveHandlersContext.Provider value={registry}>
      {children}
    </NoteSaveHandlersContext.Provider>
  );
};

export const useNoteSaveHandlerRegistry = () =>
  useContext(NoteSaveHandlersContext);

export const useRegisterNoteSaveHandler = (name, handler) => {
  const registry = useNoteSaveHandlerRegistry();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!registry) return undefined;

    return registry.register(name, (...args) => handlerRef.current?.(...args));
  }, [registry, name]);
};
